<script setup lang="ts">
import type { CarouselProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import { computed } from 'vue';

import {
    AuctionState,
    DirectSaleState,
    getApiAuctionsListingQueryKey,
    getApiDirectSalesListingQueryKey,
    type OrderByFieldDto,
    SalesListingSortBy,
    SortOrder,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import type { SaleListingItemEntity } from '~/types/sale.type';

export interface StoryblokSaleListingCarouselProps {
    blok: StoryblokSaleListingCarousel;
}

// PROPS
const { blok } = defineProps<StoryblokSaleListingCarouselProps>();

type CarouselItemEntity =
    | {
          variant: 'skeleton';
      }
    | {
          variant: 'sale';
          sale: SaleListingItemEntity;
      };

// DATA
const AUCTION_KINDS: ReadonlySet<StoryblokSaleListingCarousel['kind']> = new Set([
    'ending_soon_auctions',
    'featured_auctions',
    'featured_mixed',
    'trending_auctions',
]);
const DIRECT_SALE_KINDS: ReadonlySet<StoryblokSaleListingCarousel['kind']> = new Set([
    'featured_directSales',
    'featured_mixed',
    'trending_directSales',
]);
const KIND_TO_ORDER_BY_MAP: Record<StoryblokSaleListingCarousel['kind'], OrderByFieldDto[]> = {
    ending_soon_auctions: [{ field: SalesListingSortBy.expiresAt, order: SortOrder.desc }],
    featured_auctions: [],
    featured_directSales: [],
    featured_mixed: [],
    trending_auctions: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
    trending_directSales: [
        { field: SalesListingSortBy.priority, order: SortOrder.asc },
        { field: SalesListingSortBy.startAt, order: SortOrder.asc },
    ],
};
const isAuctionEnabled = computed(() => AUCTION_KINDS.has(blok.kind));
const isDirectSaleEnabled = computed(() => DIRECT_SALE_KINDS.has(blok.kind));
const salesCount = computed(() => (blok.salesCount ? Number(blok.salesCount) : 8));
const auctionApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [AuctionState.Running],
    itemsPerPage: salesCount.value,
}));

const {
    data: auctionData,
    isLoading: isLoadingAuctions,
    isEnabled: isEnabledAuctions,
} = useApiAuctionsListing(auctionApiParams, {
    query: { enabled: computed(() => import.meta.client && isAuctionEnabled.value), queryKey: getApiAuctionsListingQueryKey(auctionApiParams) },
});

const directSalesApiParams = computed(() => ({
    orderBy: KIND_TO_ORDER_BY_MAP[blok.kind],
    state: [DirectSaleState.Open],
    itemsPerPage: salesCount.value,
}));
const {
    data: directSaleData,
    isLoading: isLoadingDirectSales,
    isEnabled: isEnabledDirectSales,
} = useApiDirectSalesListing(directSalesApiParams, {
    query: {
        enabled: computed(() => import.meta.client && isDirectSaleEnabled.value),
        queryKey: getApiDirectSalesListingQueryKey(directSalesApiParams),
    },
});

// METHODS
const mapSale = (sale: SaleListingItemEntity): CarouselItemEntity => ({ variant: 'sale', sale });

const items = computed<CarouselItemEntity[]>(() => {
    const auctionsPending = isAuctionEnabled.value && (isLoadingAuctions.value || !isEnabledAuctions.value);
    const directSalesPending = isDirectSaleEnabled.value && (isLoadingDirectSales.value || !isEnabledDirectSales.value);

    if (auctionsPending || directSalesPending) {
        return Array.from(Array(salesCount.value)).map<CarouselItemEntity>(() => ({ variant: 'skeleton' }));
    }

    const auctions = isAuctionEnabled.value ? (auctionData.value?.auctions ?? []) : [];
    const directSales = isDirectSaleEnabled.value ? (directSaleData.value?.directSales ?? []) : [];

    return [...auctions, ...directSales].slice(0, salesCount.value).map(mapSale);
});

const carouselProps = computed<Omit<CarouselProps, 'items'>>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokSaleListingCarousel>(blok), ['_uid', 'component', 'kind', 'title', 'salesCount', 'itemsPerPage'])
);
const ui = useStoryblokCarouselCarouselUi(() => blok.itemsPerPage ?? '4');
</script>

<template>
    <div v-if="items.length" v-editable="blok" class="space-y-4">
        <h4 v-if="!!blok.title" class="text-center text-xl font-semibold">{{ blok.title }}</h4>
        <UCarousel v-slot="{ item }" v-bind="carouselProps" :items="items" :ui="ui">
            <SlpCardSkeleton v-if="item.variant === 'skeleton'" />
            <SlpCard v-else :sale="item.sale" />
        </UCarousel>
    </div>
</template>
